/**
 * Combobox Component V2
 *
 * Kombiniert Popover + Command für durchsuchbare Dropdown-Listen
 * WCAG 2.1 Level AA + BFSG konform
 *
 * Features:
 * - Durchsuchbar mit Tastatur-Navigation
 * - Scrollbare Liste
 * - BFSG-konforme Touch-Targets (44px)
 *
 * @example
 * ```tsx
 * <Combobox
 *   label="Template"
 *   placeholder="Template wählen..."
 *   searchPlaceholder="Suchen..."
 *   emptyText="Keine Templates gefunden"
 *   options={[
 *     { value: 'lifestyle', label: 'Lifestyle' },
 *     { value: 'packshot', label: 'Packshot' },
 *   ]}
 *   value={template}
 *   onValueChange={setTemplate}
 * />
 * ```
 */

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './Command';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  imageUrl?: string | null;
}

export interface ComboboxProps {
  /** Label für das Feld */
  label?: string;
  /** Placeholder im Button wenn nichts ausgewählt */
  placeholder?: string;
  /** Placeholder im Suchfeld */
  searchPlaceholder?: string;
  /** Text wenn keine Ergebnisse */
  emptyText?: string;
  /** Optionen */
  options: ComboboxOption[];
  /** Aktueller Wert */
  value?: string;
  /** Callback bei Änderung */
  onValueChange?: (value: string) => void;
  /** Deaktiviert */
  disabled?: boolean;
  /** Fehlermeldung */
  error?: string;
  /** Hilfetext */
  helperText?: string;
  /** Pflichtfeld */
  required?: boolean;
  /** Custom className */
  className?: string;
  /** Custom rendering function for options */
  renderOption?: (option: ComboboxOption) => React.ReactNode;
}

export function Combobox({
  label,
  placeholder,
  searchPlaceholder,
  emptyText,
  options,
  value,
  onValueChange,
  disabled,
  error,
  helperText,
  required,
  className,
  renderOption,
}: ComboboxProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.select');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('common.search');
  const resolvedEmptyText = emptyText ?? t('common.noResults');
  const [open, setOpen] = React.useState(false);

  const selectedOption = options.find((opt) => opt.value === value);
  const hasError = Boolean(error);

  return (
    <div className={cn('w-full', className)}>
      {/* Label */}
      {label && (
        <label
          className={cn(
            'block text-sm font-medium mb-1.5',
            'text-neutral-700 dark:text-neutral-300',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {label}
          {required && (
            <span className="text-red-600 dark:text-red-400 ml-1" aria-label={t('common.required')}>
              *
            </span>
          )}
        </label>
      )}

      {/* Combobox */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-invalid={hasError}
            disabled={disabled}
            className={cn(
              'flex w-full items-center justify-between',
              'rounded-md border px-3 py-2',
              'bg-white dark:bg-neutral-900 text-sm',
              'text-neutral-900 dark:text-neutral-100',
              // BFSG: Touch targets minimum 44x44px (h-11 = 44px)
              'min-h-11',
              // Focus styles
              'focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-offset-2',
              'focus-visible:ring-primary-500 dark:focus-visible:ring-offset-neutral-900',
              // Disabled state
              'disabled:cursor-not-allowed disabled:opacity-50',
              // Border color
              hasError
                ? 'border-red-600 dark:border-red-500'
                : 'border-neutral-300 dark:border-neutral-700',
              // Placeholder color
              !selectedOption && 'text-neutral-400 dark:text-neutral-500'
            )}
          >
            <span className="flex items-center gap-2 min-w-0 truncate">
              {selectedOption?.imageUrl && (
                <img
                  src={selectedOption.imageUrl}
                  alt=""
                  className="h-6 w-6 rounded object-cover shrink-0"
                />
              )}
              {selectedOption?.label || resolvedPlaceholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={resolvedSearchPlaceholder} />
            <CommandList>
              <CommandEmpty>{resolvedEmptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label} // cmdk filtert nach diesem Wert
                    disabled={option.disabled}
                    onSelect={() => {
                      onValueChange?.(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <div className="flex items-center gap-2">
                        {option.imageUrl && (
                          <img
                            src={option.imageUrl}
                            alt=""
                            className="h-8 w-8 rounded object-cover shrink-0 transition-all duration-200 hover:h-[80px] hover:w-[80px]"
                          />
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{option.label}</span>
                          {option.description && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                              {option.description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Error Message */}
      {error && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      {/* Helper Text */}
      {!error && helperText && (
        <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
          {helperText}
        </p>
      )}
    </div>
  );
}

Combobox.displayName = 'Combobox';
